import {apiRequest} from './client';
import type {
    CreateSiteRequest,
    Site,
    SiteStatusSimple,
    SiteWithAccess,
    SlugCheckResponse,
    SlugRenameResult
} from '@/types/api';

export type SiteWithDeletion = SiteWithAccess & {
    deletion_pending?: boolean;
    deletion_state?: string | null;
};

export type DeletionResult = {
    status?: 'deletion_pending';
    state?: string;
    remaining?: Record<string, unknown>;
    message?: string;
};

export const SITE_DELETION_PENDING_MESSAGE = 'Deletion started. Your site will be removed once its connected marketing tools have been cleaned up.';

export async function getSites(): Promise<SiteWithDeletion[]> {
    return apiRequest<SiteWithDeletion[]>('/sites', {
        method: 'GET'
    });
}

export async function createSite(data: CreateSiteRequest): Promise<Site> {
    return apiRequest<Site>('/sites', {
        method: 'POST',
        body: JSON.stringify(data)
    });
}

export async function getSite(id: string): Promise<SiteWithDeletion> {
    return apiRequest<SiteWithDeletion>(`/sites/${id}`, {
        method: 'GET'
    });
}

export async function getSiteStatus(id: string): Promise<SiteStatusSimple> {
    return apiRequest<SiteStatusSimple>(`/sites/${id}/status`, {
        method: 'GET'
    });
}

export async function deleteSite(id: string): Promise<DeletionResult> {
    return apiRequest<DeletionResult>(`/sites/${id}`, {
        method: 'DELETE'
    });
}

export async function retrySite(id: string): Promise<Site> {
    return apiRequest<Site>(`/sites/${id}/retry`, {
        method: 'POST'
    });
}

export async function checkSlug(slug: string): Promise<SlugCheckResponse> {
    return apiRequest<SlugCheckResponse>(`/sites/check-slug/${slug}`, {
        method: 'GET'
    });
}

/**
 * Owner-scoped availability check used by the change-address dialog. Unlike
 * `checkSlug`, this one treats the site's own historical names as available,
 * so an owner can move back to an address they used before.
 */
export async function checkSiteSlug(siteId: string, slug: string): Promise<SlugCheckResponse> {
    return apiRequest<SlugCheckResponse>(`/sites/${siteId}/slug-available/${encodeURIComponent(slug)}`, {
        method: 'GET'
    });
}

export async function renameSiteSlug(siteId: string, slug: string): Promise<SlugRenameResult> {
    return apiRequest<SlugRenameResult>(`/sites/${siteId}/slug`, {
        method: 'POST',
        body: JSON.stringify({slug})
    });
}

export async function getSSOLink(siteId: string, returnTo?: string): Promise<string> {
    const query = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
    const data = await apiRequest<{ url: string }>(`/sites/${siteId}/sso-link${query}`, {
        method: 'GET'
    });
    return data.url;
}
